// scheduling/services/schedData.js
// Read service for scheduler data — mirrors client/src/services/data.js pattern

const SCHED_BASE = '/api/schedData';

async function fetchSchedData(type, body = {}) {
  const url = `${SCHED_BASE}?type=${encodeURIComponent(type)}`;
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.text();
    throw new Error(`schedData(${type}) ${r.status}: ${err}`);
  }
  return r.json();
}

// Several reads in ONE request. `parts` is [{ type, args }]; resolves to
// { data: { [type]: result }, errors: { [type]: message } }.
//
// Prefer this over a Promise.all of individual fetches on a page load. Those cost one
// HTTP round trip each AND one Function invocation each, and the SWA's managed functions
// share a single small worker pool across every /api/* route — so a 10-fetch page load
// held 10 workers and made concurrent reads and writes queue behind it.
//
// Per-part failures are isolated: a failed part shows up in `errors` and is absent from
// `data`, so callers keep their own fallbacks (e.g. `?? []`) for optional datasets.
export const fetchBootstrap = (parts) => fetchSchedData('bootstrap', { parts });

// opts.includeScheduledInactive — also return inactive staff who still hold scheduled
// hours today-onward. Grid-only; every other caller passes nothing and gets active only.
export const fetchStaff = (opts) => fetchSchedData('staff', opts || {});
export const fetchClients = (filters) => fetchSchedData('clients', filters);
export const fetchJobs = (filters) => fetchSchedData('jobs', filters);
export const fetchSchedule = (range) => fetchSchedData('schedule', range);
// Which (staff, job) groups spill outside the fetched window, and their true totals.
// The grid fetches `schedule` for exactly the visible window, so this is the only way to
// know a bar is clipped rather than finished.
export const fetchScheduleSpans = (range) => fetchSchedData('schedule_spans', range);
export const fetchConflicts = (range) => fetchSchedData('conflicts', range);
export const fetchUtilization = (range) => fetchSchedData('utilization', range);
export const fetchExperience = (staffId) => fetchSchedData('experience', { staffId });
export const fetchExperienceRanking = (dimension, dimensionName, dimensionId) => fetchSchedData('experience_ranking', { dimension, dimensionName, dimensionId });
export const fetchDependencies = (jobId) => fetchSchedData('dependencies', { jobId });
export const fetchSuggestions = (jobId, opts) => fetchSchedData('suggestions', { jobId, ...(opts || {}) });
export const fetchCertifications = (staffId) => fetchSchedData('certifications', { staffId });
export const fetchInterests = (staffId) => fetchSchedData('interests', { staffId });
export const fetchSkills = (staffId) => fetchSchedData('skills', { staffId });
export const fetchNotifications = (staffId, opts) => fetchSchedData('notifications', { staffId, ...opts });
export const fetchCalendars = (staffId) => fetchSchedData('calendars', { staffId });
// Trello #332 — capacity drift vs PE. Super-user only; returns
// { today, activeStaff, counts, rows[] } where each row carries the resolver's steps.
export const fetchCapacityDrift = () => fetchSchedData('capacity_drift');
// Trello #332 — blast radius of a pending capacity edit, for the save confirm-gate:
// { willReachPE, blocker, fanOut, affected[], approvedPTO[] }.
export const fetchCapacityImpact = (tpl) => fetchSchedData('capacity_impact', tpl);
// Jobs PE has completed that STILL hold today-onward schedule entries. peSync retains the
// job row now instead of deleting it (and its entries) on close, so future hours can outlive
// the job they were booked against. Scope with { managerId } for My Jobs, { staffId } for a
// staffer's own stranded time (populates MyFutureEntries/MyFutureHours), or neither for the
// firm-wide admin sweep.
export const fetchClosedJobsWithSchedule = (filters) => fetchSchedData('closed_jobs_with_schedule', filters || {});
export const fetchJobsHistory = (filters) => fetchSchedData('jobs_history', filters);
export const fetchJobsHistoryWIP = (filters) => fetchSchedData('jobs_history_wip', filters);
export const fetchJobsHistoryRoles = (peJobIndex) => fetchSchedData('jobs_history_roles', { peJobIndex });
export const fetchSavedFilters = (email) => fetchSchedData('saved_filters', { email });
export const fetchAssessments = (staffId) => fetchSchedData('assessments', { staffId });
export const fetchRecConfig = () => fetchSchedData('rec_config');
export const fetchSuggestionAvailability = (staffIds, startDate, numWeeks) => fetchSchedData('suggestion_availability', { staffIds, startDate, numWeeks });
export const fetchJobsWIP = (filters) => fetchSchedData('jobs_wip', filters);
export const fetchJobProgress = (today) => fetchSchedData('job_progress', { today });
export const fetchJobScheduledStaff = (filters) => fetchSchedData('job_scheduled_staff', filters);
export const fetchConflictDetails = (staffId, weekStart) => fetchSchedData('conflict_details', { staffId, weekStart });
// Job history: fetchScheduleHistory(jobId) or ({ jobId }). Person history: ({ staffId }).
export const fetchScheduleHistory = (arg) => fetchSchedData('schedule_history',
  (arg && typeof arg === 'object') ? arg : { jobId: arg });
export const fetchSyncSummary = () => fetchSchedData('sync_summary', {});
export const fetchStatusOrder = () => fetchSchedData('status_order', {});
export const fetchPTO = (range) => fetchSchedData('pto', range);
export const fetchHolidays = () => fetchSchedData('holidays');
export const fetchGlobalAssignments = () => fetchSchedData('global_assignments');
export const fetchPlaceholders = (filters) => fetchSchedData('placeholders', filters || {});
export const fetchCustomJobs = (filters) => fetchSchedData('custom_jobs', filters || {});

// Gen Track tax/assurance bands for the Staff Suggestions badge (Trello #309).
// Lives here rather than in reportsApi because the badge's audience is anyone doing the
// scheduling — rprsReports gates report.gentrack to superusers, so serving it from
// there would mean only DJB ever saw the flag.
// → { bands: [{ staffId, taxPct, totalHours }], taxCodes, assuranceCodes, taxDescs, assuranceDescs }
export const fetchGenTrackBands = (today) => fetchSchedData('gentrack_bands', { today });
export const fetchIDDJobs = (filters) => fetchSchedData('idd_jobs', filters);
export const fetchDataIntegrityCycles = () => fetchSchedData('data_integrity_cycles');
export const fetchJobTaskBudgets = (peJobIndex, cycleName) => fetchSchedData('job_task_budgets', { peJobIndex, cycleName });
// Many jobs in one round trip (<= 1000 ids per call; the server scopes to the caller's jobs).
export const fetchJobTaskBudgetsBatch = (peJobIndexes, cycleName) => fetchSchedData('job_task_budgets', { peJobIndexes, cycleName });
// Model-suggested budgets (return_job_ml product 2), one call per page load. Same scope as
// idd_jobs. Callers must .catch(() => []): the tab works without them (PY fallback).
export const fetchJobPredictions = (filters) => fetchSchedData('job_predictions', filters);
export const fetchJMWeeklyHoursPY = (managerId) => fetchSchedData('jm_weekly_hours_py', { managerId });
export const fetchJobWeeklyWIP = (peJobIndex) => fetchSchedData('job_weekly_wip', { peJobIndex });
export const fetchIDDSubmittedUsers = (cycleName) => fetchSchedData('idd_submitted_users', { cycleName });
export const fetchIDDHistory = (peJobIndex, cycleName) => fetchSchedData('idd_history', { peJobIndex, cycleName });
export const fetchIDDProgressSummary = (managerStaffId, cycleName) => fetchSchedData('idd_progress_summary', { managerStaffId, cycleName });
export const fetchIDDCycleProgress = (cycleName) => fetchSchedData('idd_cycle_progress', { cycleName });
// Non-Billable Budget (Stage 28D) — read-side service exports.
export const fetchNonBillCategories = () => fetchSchedData('nonbill_categories');
export const fetchNonBillRules = () => fetchSchedData('nonbill_rules');
export const fetchNonBillStandards = () => fetchSchedData('nonbill_standards');
export const fetchNonBillStandardOverrides = () => fetchSchedData('nonbill_standard_overrides');
export const fetchNonBillMyBudget = (staffId, fiscalYear) => fetchSchedData('nonbill_my_budget', { staffId, fiscalYear });
export const fetchNonBillProgressSummary = (staffId, fiscalYear) => fetchSchedData('nonbill_progress_summary', { staffId, fiscalYear });
export const fetchNonBillCycleProgress = (cycleName, fiscalYear) => fetchSchedData('nonbill_cycle_progress', { cycleName, fiscalYear });
export const fetchNonBillSubmittedUsers = (fiscalYear) => fetchSchedData('nonbill_submitted_users', { fiscalYear });

// Job Pools claim/assign day markers for the grid (Trello #257). Read-only
// visuals — these are event stamps, never schedule entries, and must not feed any
// capacity or hours calculation.
export const fetchPoolEvents = (range) => fetchSchedData('pool_events', range || {});
